import { EnableCellControls } from "@/appComponents/Navigation/ContextualToolbar/ToolbarOptions";
import * as StateLib from "@/Utils/StateLib";
import { GetWSMgr } from "@/Utils/StateLib";
import {
  enumChartElement,
  enumChartBoxInteractionMode,
  enumCellContentType,
  enumHierarchyTypes
} from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";

export const InsertCellField = option => {
  const wsMgr = StateLib.GetWSMgr();
  const canvasPanel = wsMgr?.fCanvasPanel;
  let firstSelectedCell = null;
  let firstSelectedGutter = null;
  if (wsMgr) {
    firstSelectedCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    firstSelectedGutter = wsMgr.GetFirstSelectedElement(enumChartElement.gutterCell);
  }

  if (firstSelectedGutter != null) return;

  const field = option.fieldDef;
  const value = option.value;
  const targetCell = firstSelectedCell;

  if (value === "freetext") {
    targetCell.SetField(null);
    targetCell.SetCellContentType(enumCellContentType.FreeText);
    targetCell.SetCellMode(enumChartBoxInteractionMode.layout);
    wsMgr.StartEditCellContent(targetCell.GetParentPanelRef(), targetCell);
  } else if (field === "none") {
    // Clear field; shared tail below emits OnLayoutMutated + rebuild once.
    targetCell.SetField(null);
    targetCell.SetCellContentType(enumCellContentType.Blank);
  } else {
    targetCell.SetField(field);
    if (targetCell.IsPicture()) {
      targetCell.SetupPictureCell();
    }
  }

  const layout = targetCell.GetLayout();
  const layoutPanel = wsMgr.fLayoutPanel;
  if (layoutPanel && Globals._uiConfig.InBoxLayoutEditorMode()) {
    layoutPanel.BuildLayout(layout, true);
  } else {
    canvasPanel.UpdateLayoutBackgroundMgr(layout);
  }

  // Self-gates outside box-style BLE; matches the row/col ops in QLayoutPanel.
  wsMgr.OnLayoutMutated?.();
  StateLib.ReRenderCanvas();
};

export const DisableAnyInsert = () => {
  const wsMgr = StateLib.GetWSMgr();
  let firstSelectedCell = null;
  let firstSelectedGutter = null;
  if (wsMgr) {
    firstSelectedCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    firstSelectedGutter = wsMgr.GetFirstSelectedElement(enumChartElement.gutterCell);
  }
  let isGutter = firstSelectedGutter != null;
  let enabled = firstSelectedCell && !isGutter;
  return !enabled;
};

export const CheckBoxCellControlEnabled = value => {
  let enabled = false;
  const {
    topInsRow,
    bottomInsRow,
    leftInsCol,
    rightInsCol,
    enableDelCols,
    enableDelRows,
    enableMergeCells,
    enableUnmerge
  } = EnableCellControls();
  switch (value) {
    case "insertrowabove":
      enabled = topInsRow;
      break;
    case "insertrowbelow":
      enabled = bottomInsRow;
      break;
    case "insertcolleft":
      enabled = leftInsCol;
      break;
    case "insertcolright":
      enabled = rightInsCol;
      break;
    case "deletecols":
      enabled = enableDelCols;
      break;
    case "deleterows":
      enabled = enableDelRows;
      break;
    case "mergeall":
      enabled = enableMergeCells;
      break;
    default:
      return true;
  }
  return enabled;
};

export const CheckBoxCellControlDisabled = value => {
  return !CheckBoxCellControlEnabled(value);
};

export const InsertRow = (insertAbove = true) => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoRowInsert(insertAbove);
  StateLib.ReRenderCanvas();
};

export const InsertCol = (insertLeft = true) => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoColInsert(insertLeft);
  StateLib.ReRenderCanvas();
};

// Delete

export const DeleteRow = () => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoRowDelete();
  StateLib.ReRenderCanvas();
};

export const DeleteColumn = () => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoColumnDelete();
  StateLib.ReRenderCanvas();
};

export const DeleteLayoutField = () => {
  const wsMgr = StateLib.GetWSMgr();
  let firstSelectedCell = null;
  let firstSelectedGutter = null;
  if (wsMgr) {
    firstSelectedCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    firstSelectedGutter = wsMgr.GetFirstSelectedElement(enumChartElement.gutterCell);
  }

  let isGutter = firstSelectedGutter != null;

  if (firstSelectedCell && !isGutter) {
    firstSelectedCell.SetField(null);
    firstSelectedCell.SetCellContentType(enumCellContentType.Blank);
    wsMgr.OnLayoutMutated?.();
  }
  StateLib.ReRenderCanvas();
};

export const DisableFieldDelete = () => {
  let disabled = true;
  const wsMgr = StateLib.GetWSMgr();
  let firstSelectedCell = null;
  let firstSelectedGutter = null;
  if (wsMgr) {
    firstSelectedCell = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
    firstSelectedGutter = wsMgr.GetFirstSelectedElement(enumChartElement.gutterCell);
  }

  let isGutter = firstSelectedGutter != null;

  if (firstSelectedCell && !isGutter) {
    var cell = firstSelectedCell.GetCellDesc();
    disabled = cell?.GetCellContentType() != enumCellContentType.Field;
  }
  return disabled;
};

// Merge
export const IsMergeEnabled = type => {
  const { enableUnmergeAllCells, enableMergeAny, enableMergeAllCells } = EnableCellControls();
  switch (type) {
    case "mergecells":
    case "mergeany":
      return enableMergeAny;
    case "unmergecells":
      return enableUnmergeAllCells;
    case "mergeall":
      return enableMergeAllCells;
    default:
      return false;
  }
};

export const IsMergeDisabled = type => {
  return !IsMergeEnabled(type);
};

export const DoMergeCells = () => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoMergeCells();
  StateLib.ReRenderCanvas();
};

export const ToggleMergeCells = () => {
  const wsMgr = GetWSMgr();
  const selection = wsMgr.GetFirstSelectedElement(enumChartElement.boxCell);
  if (!selection) return;
  const cellDesc = selection.GetCellDesc();
  if (cellDesc.IsMergedCell()) {
    DoUnmergeCells();
  } else {
    DoMergeCells();
  }
};

export const DoUnmergeCells = () => {
  const wsMgr = StateLib.GetWSMgr();
  wsMgr.DoUnmergeCells();
  StateLib.ReRenderCanvas();
};

export const GetLayoutObjClass = () => {
  const wsMgr = StateLib.GetWSMgr();
  const layoutPanel = wsMgr?.fLayoutPanel;
  if (!layoutPanel) return null;

  const layout = layoutPanel.GetLayout();
  const hierarchyType = layout?.GetHierarchyType();
  if (!hierarchyType) return null;

  const doc = wsMgr.GetDoc();
  const objDb = doc?.GetObjDB();
  if (!objDb) return null;

  const classNameMap = {
    [enumHierarchyTypes.employees]: "Box",
    [enumHierarchyTypes.positions]: "Position"
  };

  return objDb.GetClassByName(classNameMap[hierarchyType]);
};
